// Static skill-context lookup. The JSON is built by
// tools/skill-merge/tier2.py — for each skill, Claude generates a
// 60-word interpretation anchored in the actual co-occurring skills
// declared by the same people. Used to power hover tooltips so a user
// hovering "Pipelines" sees "Art/VFX asset pipelines" rather than
// guessing whether it means CI/CD.
//
// Lookups return null for unknown skill ids — brand-new skills that
// haven't been enriched yet just render without a tooltip. Run the
// enrichment to fill the gap; the bundle picks it up on next build.
//
// Source of truth lives in tools/skill-merge/contexts/skill-contexts.json;
// tier2.py mirrors it here on every save so the frontend always has
// the latest cards without an explicit copy step.
import contextsData from 'data/skill-contexts.json';
import { readSkillContextOverrides } from './api-client';

export interface SkillContext {
  skill_id: string;
  name: string;
  meaning: string;
  category: string;
  confidence: 'high' | 'medium' | 'low';
  alternatives: string[];
  co_occurring: string[];
}

const DATA = contextsData as unknown as Record<string, SkillContext>;

// Server-side overrides hydrated once at app boot via
// `hydrateSkillContextOverrides`. Each entry replaces the `meaning`
// field on the bundled card; the rest (category, confidence,
// alternatives, co_occurring) stays from the LLM baseline since
// those don't change with a manual edit.
const OVERRIDES: Record<string, string> = {};

/**
 * Boot-time hydration: pulls all server-side overrides and merges
 * them into the runtime cache. Call once from auth-context after
 * the user is loaded. Failures are swallowed — the bundled JSON is
 * a perfectly fine fallback (just stale).
 */
export async function hydrateSkillContextOverrides(): Promise<void> {
  try {
    const overrides = await readSkillContextOverrides();
    // Replace, don't merge. If we only ever set keys, an admin
    // deleting an override server-side wouldn't propagate to clients
    // that already hydrated — they'd keep showing the deleted text
    // until a hard refresh.
    for (const key of Object.keys(OVERRIDES)) delete OVERRIDES[key];
    for (const o of overrides || []) {
      if (o.skill_id && o.meaning) OVERRIDES[o.skill_id] = o.meaning;
    }
  } catch {
    // Silent: bundled JSON remains the source of truth.
  }
}

/**
 * Return the context card for `skillId`, or null if no card exists
 * (skill hasn't been enriched yet, or the id is bogus). Sync, O(1)
 * map lookup — safe to call from hot render paths. If a server-side
 * override exists, its `meaning` shadows the bundled one.
 */
export function getSkillContext(skillId: string | undefined | null): SkillContext | null {
  if (!skillId) return null;
  const base = DATA[skillId] ?? null;
  const override = OVERRIDES[skillId];
  if (!base && !override) return null;
  if (override && base) return { ...base, meaning: override };
  if (override && !base) {
    // Override exists for a skill the bundle doesn't know about
    // (e.g. a brand-new skill someone proposed a description for).
    // Synthesise a minimal card so the tooltip still renders.
    return {
      skill_id: skillId,
      name: '',
      meaning: override,
      category: '',
      confidence: 'medium',
      alternatives: [],
      co_occurring: [],
    };
  }
  return base;
}
